import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Printer } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

export default function Ledger() {
  const { partyType, partyId } = useParams();
  const navigate = useNavigate();
  const { activeCompanyId, activeStoreId } = useAuth();
  const [data, setData] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  useEffect(() => {
    if (!activeCompanyId || !partyId) return;
    api.get(`/ledger/${partyType}/${partyId}`).then((r) => setData(r.data));
  }, [activeCompanyId, activeStoreId, partyType, partyId]);

  const filtered = useMemo(() => {
    if (!data) return { entries: [], totals: { debit: 0, credit: 0, balance: 0 } };
    const src = data.entries.filter((e) => {
      const d = (e.date || "").slice(0, 10);
      return (!start || d >= start) && (!end || d <= end);
    });
    let debit = 0, credit = 0;
    const withBal = src.map((e) => {
      debit += Number(e.debit || 0);
      credit += Number(e.credit || 0);
      return e;
    });
    return { entries: withBal, totals: { debit, credit, balance: debit - credit } };
  }, [data, start, end]);

  if (!data) return <div className="page-shell text-sm text-muted-foreground">Loading ledger…</div>;

  const isCustomer = partyType === "customer";
  const bal = data.totals.balance;

  return (
    <div className="page-shell space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} />
          </Button>
          <div>
            <div className="eyebrow">{isCustomer ? "Customer" : "Supplier"} Ledger</div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">{data.party.name}</h1>
            <div className="text-[12px] text-muted-foreground mt-1 font-mono">
              {data.party.gstin && <>GSTIN: {data.party.gstin} · </>}
              {data.party.phone && <>{data.party.phone} · </>}
              {data.party.email}
            </div>
          </div>
        </div>
        <Button variant="outline" className="h-9" onClick={() => window.print()}>
          <Printer size={14} className="mr-1.5" /> Print
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="kpi">
          <div className="eyebrow">Total {isCustomer ? "Invoiced" : "Purchased"}</div>
          <div className="val">{inr(isCustomer ? data.totals.debit : data.totals.credit)}</div>
        </div>
        <div className="kpi">
          <div className="eyebrow">Total {isCustomer ? "Received" : "Paid"}</div>
          <div className="val">{inr(isCustomer ? data.totals.credit : data.totals.debit)}</div>
        </div>
        <div className="kpi">
          <div className="eyebrow">Closing Balance</div>
          <div className="val" style={{ color: bal > 0 ? "hsl(var(--destructive))" : bal < 0 ? "hsl(var(--success))" : undefined }}>
            {inr(Math.abs(bal))}
          </div>
          <div className="text-[11px] font-mono mt-1 text-muted-foreground uppercase tracking-wider">
            {bal > 0 ? (isCustomer ? "Receivable" : "Payable") : bal < 0 ? "Advance" : "Settled"}
          </div>
        </div>
        <div className="kpi">
          <div className="eyebrow">Credit Limit</div>
          <div className="val">{isCustomer ? inr(data.party.credit_limit) : "—"}</div>
        </div>
      </div>

      <div className="card-flat p-3 flex flex-wrap items-end gap-3 no-print">
        <div className="space-y-1.5">
          <Label className="eyebrow">From Date</Label>
          <Input type="date" min="1900-01-01" value={start} onChange={(e) => setStart(e.target.value)}
            className="h-9 font-mono w-40" data-testid="ledger-start" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">To Date</Label>
          <Input type="date" min="1900-01-01" value={end} onChange={(e) => setEnd(e.target.value)}
            className="h-9 font-mono w-40" data-testid="ledger-end" />
        </div>
        <Button variant="outline" size="sm" className="h-9"
          onClick={() => { setStart(""); setEnd(""); }} data-testid="ledger-clear-btn">
          Show All
        </Button>
        <div className="text-[12px] text-muted-foreground ml-auto">
          Showing {filtered.entries.length} of {data.entries.length} entries
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Ref</th>
              <th>Description</th>
              <th className="num">Debit</th>
              <th className="num">Credit</th>
              <th className="num">Balance</th>
            </tr>
          </thead>
          <tbody>
            {filtered.entries.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">
                {data.entries.length === 0 ? "No transactions yet." : "No entries in the selected date range."}
              </td></tr>
            ) : filtered.entries.map((e, i) => (
              <tr key={e.id || e.voucher_id || `${e.date}-${e.number || i}`}>
                <td className="font-mono text-[12px]">{e.date}</td>
                <td className="font-mono text-[12px]">{e.ref}</td>
                <td>{e.description}</td>
                <td className="num">{e.debit ? inr(e.debit) : "—"}</td>
                <td className="num">{e.credit ? inr(e.credit) : "—"}</td>
                <td className="num font-semibold">{inr(Math.abs(e.balance))} {e.balance !== 0 && (e.balance > 0 ? "Dr" : "Cr")}</td>
              </tr>
            ))}
            <tr className="bg-secondary/60 font-semibold">
              <td colSpan={3}>{start || end ? `PERIOD TOTAL` : `TOTAL`}</td>
              <td className="num">{inr(filtered.totals.debit)}</td>
              <td className="num">{inr(filtered.totals.credit)}</td>
              <td className="num">{inr(Math.abs(filtered.totals.balance))} {filtered.totals.balance !== 0 && (filtered.totals.balance > 0 ? "Dr" : "Cr")}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
